<template>
	<v-card
		:height="height"
		class="variable-viewer-card sgnl-flex sgnl-flex-column sgnl-justify-space-between"
		data-test="variable-viewer-panel"
	>
		<div
			class="variable-viewer-header sgnl-flex sgnl-align-center sgnl-px-12 sgnl-paragraph-m-medium sgnl-text-color-default"
		>
			<div class="sgnl-flex-grow-1 sgnl-flex sgnl-align-center sgnl-gap-8">
				<span>Variable Viewer</span>
				<DisclosureIcon
					disclosure-text="This panel shows the variables declared in your application. When you click the 'Discover variables' button the Signaloid Cloud Developer Platform runs your application to identify the variables in your application. This run will show up in your Task Manager as a reference core run."
				/>
			</div>
			<div class="sgnl-flex">
				<TooltipButton
					:icon="true"
					btnClass="sgnl-mx-0 sgnl-px-0"
					@click="resetVariableMarks"
					:disabled="variableIDsToTrace.length == 0"
					tooltipText="Reset marked variables"
					data-test="reset-marked-variables-button"
				>
					<template #btnContent>
						<DataBackup16 />
					</template>
				</TooltipButton>
				<TooltipButton
					v-if="disableRequestPanelClose === false"
					:icon="true"
					btnClass="sgnl-mx-0 sgnl-px-0"
					@click="requestPanelClose"
					tooltipText="Close Variable Viewer"
					data-test="close-variable-viewer-button"
				>
					<template #btnContent>
						<Close16 />
					</template>
				</TooltipButton>
			</div>
		</div>
		<div class="sgnl-flex-grow-1 variable-viewer-body">
			<v-overlay
				absolute
				:value="variableDiscoveryInProgress"
			>
				<v-progress-circular />
			</v-overlay>
			<template v-if="showVariablePlaceholderButton">
				<LimitableActionValidator
					:limits-to-check="[UserLimitsE.DynamicInstructionCount, UserLimitsE.ConcurrentTaskCount]"
					@valid-click="requestVariableDiscovery"
				>
					<template #clickTarget>
						<SourcePlaceholderCard
							class="sgnl-m-16"
							:loading="variableDiscoveryInProgress"
							data-test="discover-variables-placeholder-button"
						>
							<template #icon>
								<SearchLocate16 />
							</template>
							<template #buttonText>Discover variables</template>
						</SourcePlaceholderCard>
					</template>
				</LimitableActionValidator>
			</template>
			<v-list
				v-else
				density="compact"
				:class="{ 'variable-list-blurred': variableDiscoveryInProgress }"
				v-model:selected="variableIDsToTrace"
				select-strategy="independent"
			>
				<v-fade-transition group>
					<AlertMessage
						v-if="variablesList.length === 0 && !variableDiscoveryInProgress"
						type="warning"
						class="sgnl-m-16"
					>
						No variables detected.
					</AlertMessage>
					<template v-else>
						<v-list-item
							v-for="(variable, index) in variablesList"
							:key="`${variable.Expression}_${String(variable.LineNumber)}_${variable.File}_${variable.Type}`"
							class="sgnl-px-8"
							data-test="discovered-variable-item"
							:value="index"
						>
							<template v-slot:prepend="{ isActive }">
								<component
									:is="isActive ? CheckboxChecked16 : Checkbox16"
									:class="[isActive ? 'icon-active' : 'icon-inactive']"
								/>
								<component
									:is="variableIcon(variable.Type)"
									:class="[isActive ? 'icon-active' : 'icon-inactive']"
								/>
							</template>
							<v-list-item-title>
								<v-tooltip
									open-delay="500"
									location="top"
								>
									<template v-slot:activator="{ props }">
										<span v-bind="props">{{ variable.Expression }}</span>
									</template>
									{{ variable.Expression }}
								</v-tooltip>
							</v-list-item-title>
							<v-list-item-subtitle>
								Line {{ variable.LineNumber }} in {{ variable.File }}
							</v-list-item-subtitle>
							<template v-slot:append>
								<TooltipButton
									variant="outlined"
									width="auto"
									@click.stop="openCustomVariableTraceDialog(variable)"
									:tooltipText="
										(variable.Type == 'UserExpression' ? 'Edit' : 'Add') +
										' Custom Trace Expression'
									"
									elevation="0"
								>
									<template #btnContent>
										<Add16 />
									</template>
								</TooltipButton>
							</template>
						</v-list-item>
					</template>
				</v-fade-transition>
			</v-list>
		</div>
		<div class="sticky-bar">
			<v-expand-transition>
				<v-alert
					v-show="showCodeUpdatedBanner"
					class="banner-info sgnl-caption-regular sgnl-my-0 clickable"
					rounded="0"
					density="compact"
					@click="requestVariableDiscovery"
				>
					Code updated. Click to refresh variables.
				</v-alert>
			</v-expand-transition>
			<v-expand-transition>
				<v-alert
					v-show="showSwitchToRefCoreBanner"
					class="banner-warning sgnl-caption-regular sgnl-my-0 clickable"
					rounded="0"
					density="compact"
					@click="requestDefaultReferenceCore"
				>
					Variable tracing requires an execution core with the <em>Reference</em> Microarchitecture. Click
					here to switch to a compatible core.
				</v-alert>
			</v-expand-transition>
			<v-expand-transition>
				<v-alert
					v-show="showPickVariablesBanner"
					class="banner-success sgnl-caption-regular sgnl-my-0"
					rounded="0"
					density="compact"
				>
					To view the variable value distributions, choose at least one variable to trace.
				</v-alert>
			</v-expand-transition>
		</div>
		<div class="variable-viewer-footer sgnl-flex sgnl-align-center">
			<LimitableActionButton
				:limits-to-check="[UserLimitsE.DynamicInstructionCount, UserLimitsE.ConcurrentTaskCount]"
				@valid-click="requestVariableDiscovery"
				:loading="variableDiscoveryInProgress"
				:disabled="discoverVariablesDisabled"
				variant="flat"
				color="primary"
				tooltipText="Discover Variables"
				width="auto"
				data-test="discover-variables-button"
			>
				<template #btnContent>
					<SearchLocate16 />
					Discover
				</template>
			</LimitableActionButton>
			<TooltipButton
				@click="openCustomVariableTraceDialog"
				variant="outlined"
				tooltipText="Add Custom Trace Expression"
				width="auto"
				data-test="add-custom-trace-expression-button"
			>
				<template #btnContent>
					<Add16 />
					<span v-if="!$vuetify.display.mobile">Add Custom Trace</span>
				</template>
			</TooltipButton>
		</div>
		<v-dialog
			:width="$vuetify.display.mobile ? '95%' : '400px'"
			v-model="showCustomVariableTraceDialog"
			@click:outside="closeCustomVariableTraceDialog"
			@keydown.esc="closeCustomVariableTraceDialog"
			@keydown.enter="requestAddCustomVariableTrace"
		>
			<v-card class="sgnl-pb-0">
				<v-card-title class="dialog-header sgnl-subtitle-medium">
					Add Custom Variable Trace
					<v-spacer />
					<v-btn
						icon
						variant="text"
						size="small"
						@click="closeCustomVariableTraceDialog"
					>
						<Close16 />
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="dialog-body">
					<v-form
						ref="customVariableTraceForm"
						v-model="customVariableTraceFormValid"
						class="stack"
					>
						<div class="input-group">
							<div class="field-label">Variable Name</div>
							<v-text-field
								class="sgnl-caption-regular"
								:rules="[rules.required]"
								v-model="customVariableTraceToAdd.Expression"
							/>
						</div>
						<div class="input-group">
							<div class="field-label">File</div>
							<v-text-field
								class="sgnl-caption-regular"
								:rules="[rules.required]"
								hint="The file in which the variable is defined."
								v-model="customVariableTraceToAdd.File"
							/>
						</div>
						<div class="input-group">
							<div class="field-label">Line number</div>
							<v-text-field
								class="sgnl-caption-regular"
								maxlength="50"
								:rules="[rules.required, rules.isNumber]"
								hint="Line number where the variable is defined."
								v-model="customVariableTraceToAdd.LineNumber"
							/>
						</div>
					</v-form>
					<v-alert
						v-if="traceDialogAlert.show"
						density="compact"
						variant="outlined"
						:type="traceDialogAlert.type"
						border="start"
						class="sgnl-my-0 sgnl-caption-regular"
					>
						{{ traceDialogAlert.text }}
					</v-alert>
				</v-card-text>
				<v-divider />
				<v-card-actions class="dialog-footer">
					<v-btn
						variant="outlined"
						@click="closeCustomVariableTraceDialog"
					>
						Cancel
					</v-btn>
					<v-btn
						variant="flat"
						color="primary"
						@click="requestAddCustomVariableTrace"
						:disabled="!customVariableTraceFormValid"
					>
						Add Trace
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script lang="ts">
import { defineComponent, markRaw, PropType } from "vue";

// Types
import { UserLimitsE } from "@/js/tierconfig";
import { TraceVariable, TraceVariableWithTraceType } from "@/types/api/nodes";

// Components
import SourcePlaceholderCard from "@/components/DataSources/SourcePlaceholderCard.vue";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";
import LimitableActionValidator from "@/components/Common/LimitableActionValidator.vue";
import DisclosureIcon from "@/components/Common/DisclosureIcon.vue";
import AlertMessage from "@/components/Common/AlertMessage.vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import DataBackup16 from "@carbon/icons-vue/es/data-backup/16";
import Add16 from "@carbon/icons-vue/es/add/16";
import Unknown16 from "@carbon/icons-vue/es/unknown/16";
import ValueVariable16 from "@carbon/icons-vue/es/value--variable/16";
import Language16 from "@carbon/icons-vue/es/language/16";
import UserProfile16 from "@carbon/icons-vue/es/user--profile/16";
import SearchLocate16 from "@carbon/icons-vue/es/search--locate/16";
import CheckboxChecked16 from "@carbon/icons-vue/es/checkbox--checked/16";
import Checkbox16 from "@carbon/icons-vue/es/checkbox/16";

type ComponentData = {
	variableIDsToTrace: number[];
	lastCodeHash: undefined | string;
	showCustomVariableTraceDialog: boolean;
	rules: {
		required: (value: any) => string | boolean;
		isNumber: (value: any) => string | boolean;
	};
	customVariableTraceToAdd: Partial<TraceVariableWithTraceType>;
	customVariableTraceFormValid: false;
	traceDialogAlert: { show: boolean; text: string; type: "warning" | "error" };
};

export default defineComponent({
	name: "VariableViewer",
	components: {
		SourcePlaceholderCard,
		TooltipButton,
		LimitableActionValidator,
		LimitableActionButton,
		DisclosureIcon,
		AlertMessage,
		Close16,
		DataBackup16,
		Unknown16,
		SearchLocate16,
		CheckboxChecked16,
		Checkbox16,
		Add16,
	},
	emits: [
		"update-variable-trace-list",
		"discover-variables",
		"close-panel",
		"switch-to-ref-core",
		"add-custom-trace-expression",
	],
	props: {
		height: { type: String, default: "100%" },
		buildAndRunLoading: { type: Boolean, default: false },
		variablesList: { type: Array as PropType<TraceVariableWithTraceType[]>, default: () => [] },
		currentCodeHash: { type: String, default: undefined },
		variableDiscoveryInProgress: { type: Boolean, default: false },
		referenceCoreChosen: { type: Boolean },
		disableRequestPanelClose: { type: Boolean, default: false },
	},
	setup() {
		return { UserLimitsE, CheckboxChecked16: markRaw(CheckboxChecked16), Checkbox16: markRaw(Checkbox16) };
	},

	data: (): ComponentData => ({
		variableIDsToTrace: [],
		lastCodeHash: undefined,
		showCustomVariableTraceDialog: false,
		rules: {
			required: (value) => !!value || "Required.",
			isNumber: (value) => {
				const number = parseInt(value);
				if (number && number > 0) {
					return true;
				} else {
					return "Should be a positive number.";
				}
			},
		},
		customVariableTraceToAdd: {
			Expression: undefined,
			Type: undefined,
			File: undefined,
			LineNumber: undefined,
		},
		customVariableTraceFormValid: false,
		traceDialogAlert: { show: false, text: "", type: "warning" },
	}),
	watch: {
		variablesList: {
			handler() {
				this.lastCodeHash = this.currentCodeHash;
				if (this.variablesList.length > 0) {
					// @ts-ignore
					this.$posthog?.capture("variable_discovery_completed", {
						variable_count: this.variablesList.length,
					});
				}
			},
		},
		variableIDsToTrace: {
			handler() {
				this.$emit("update-variable-trace-list", this.getTraceVariables());
				// @ts-ignore
				this.$posthog?.capture("variable_marked", { total_marked: this.variableIDsToTrace.length });
			},
		},
	},
	computed: {
		staleVariableList(): boolean {
			return this.currentCodeHash != this.lastCodeHash;
		},
		discoverVariablesDisabled(): boolean {
			return false;
			// temporarily disabled
			// return !(this.staleVariableList || (this.variablesList.length === 0 && this.staleVariableList));
		},
		showVariablePlaceholderButton(): boolean {
			// this.lastCodeHash is used as a proxy to determine if discover variables has been run at least once before.
			// to prevent the placeholder button replacing the "no variables found banner"
			return this.variablesList.length === 0 && this.staleVariableList && this.lastCodeHash === undefined;
		},
		showCodeUpdatedBanner(): boolean {
			// Show the banner if the variable list is stale and if discover variables has been run at least once before.
			return this.staleVariableList && this.lastCodeHash !== undefined;
		},
		showSwitchToRefCoreBanner(): boolean {
			// Show the banner if a ref core is not chosen, but no variables chosen to be traced
			return !this.referenceCoreChosen && this.variableIDsToTrace.length > 0;
		},
		showPickVariablesBanner(): boolean {
			// Show the banner if a ref core is chosen, variables have been discovered, but no variables chosen to be traced
			return this.referenceCoreChosen && this.variableIDsToTrace.length == 0 && this.variablesList.length > 0;
		},
	},
	methods: {
		getTraceVariables(): TraceVariable[] {
			const varList: TraceVariable[] = [];
			for (let idx = 0; idx < this.variableIDsToTrace.length; idx++) {
				const variableIDToTrace = this.variableIDsToTrace[idx];
				if (variableIDToTrace < this.variablesList.length) {
					const v = this.variablesList[variableIDToTrace];
					if (v.Expression && v.File && v.LineNumber) {
						varList.push({
							Expression: v.Expression,
							File: v.File,
							LineNumber: v.LineNumber,
						});
					}
				}
			}
			return varList;
		},
		resetVariableMarks() {
			this.variableIDsToTrace = [];
		},
		requestVariableDiscovery() {
			//@ts-ignore
			this.$posthog?.capture("discover_variables_button_clicked");

			this.$emit("discover-variables");
			this.resetVariableMarks();
			this.lastCodeHash = this.currentCodeHash;
		},
		requestPanelClose() {
			this.$emit("close-panel");
		},
		requestDefaultReferenceCore() {
			this.$emit("switch-to-ref-core");
		},
		variableIcon(kind) {
			switch (kind) {
				case "Variable":
					return markRaw(ValueVariable16);
				case "FormalParameter":
					return markRaw(Language16);
				case "UserExpression":
					return markRaw(UserProfile16);
				default:
					return markRaw(Unknown16);
			}
		},
		openCustomVariableTraceDialog(templateVariable: void | TraceVariableWithTraceType) {
			this.customVariableTraceToAdd = {
				Expression: templateVariable?.Expression ?? "",
				Type: "UserExpression",
				File: templateVariable?.File ?? "",
				LineNumber: templateVariable?.LineNumber ?? 1,
			};
			this.showCustomVariableTraceDialog = true;
		},
		closeCustomVariableTraceDialog() {
			this.showCustomVariableTraceDialog = false;
			// @ts-ignore FIXME:
			this.$refs.customVariableTraceForm.resetValidation();
			this.customVariableTraceToAdd = {
				Expression: undefined,
				Type: undefined,
				File: undefined,
				LineNumber: undefined,
			};
			this.traceDialogAlert = { show: false, text: "", type: "warning" };
		},
		requestAddCustomVariableTrace() {
			if (this.validTrace(this.customVariableTraceToAdd)) {
				// @ts-ignore FIXME:
				this.customVariableTraceToAdd.LineNumber = parseInt(this.customVariableTraceToAdd.LineNumber);
				this.$emit("add-custom-trace-expression", this.customVariableTraceToAdd);
				// add the newly created expression to the trace list
				this.variableIDsToTrace.push(this.variablesList.length);
				this.closeCustomVariableTraceDialog();
			} else {
				// console.log("trace exists", this.variablesList, this.customVariableTraceToAdd);
				this.traceDialogAlert = {
					show: true,
					text: `Variable trace exists.`,
					type: "error",
				};
			}
		},
		validTrace(variableTrace: Partial<TraceVariableWithTraceType>) {
			const isUnique = !this.variablesList.some((v) => {
				return (
					v.Expression == variableTrace.Expression &&
					v.LineNumber == variableTrace.LineNumber &&
					v.File == variableTrace.File
				);
			});
			let validLineNumber = false;
			// @ts-ignore FIXME:
			const lineNumber = parseInt(variableTrace.LineNumber);
			if (lineNumber && lineNumber > 0) {
				validLineNumber = true;
			}

			return isUnique && validLineNumber;
		},
	},
});
</script>
<style lang="scss" scoped>
.variable-viewer-card {
	overflow: hidden;
}

.variable-viewer-header {
	height: 2.5rem;
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	flex-shrink: 0;
	gap: var(--sgnl-space-4);

	:deep(.v-btn--icon) {
		background: transparent;

		.v-btn__overlay {
			opacity: 0;
		}
	}

	:deep(.v-btn--icon.v-btn--disabled) {
		.v-icon {
			color: var(--sgnl-default-icon-disabled);
			opacity: 1;
		}
	}
}

.variable-viewer-body {
	overflow-y: auto;
	position: relative;

	:deep(.v-list-item__prepend) {
		gap: var(--sgnl-space-8);
	}
}

.variable-list-blurred {
	filter: blur(var(--sgnl-effect-2));
}

.variable-viewer-footer {
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	flex-shrink: 0;
	padding: var(--sgnl-space-8) var(--sgnl-space-12);
	gap: var(--sgnl-space-8);
}

.banner-info {
	border-color: var(--sgnl-alert-info-border);
	background: var(--sgnl-alert-info-background-primary);
	color: var(--sgnl-alert-info-text);
}

.banner-warning {
	border-color: var(--sgnl-alert-warning-border);
	background: var(--sgnl-alert-warning-background-primary);
	color: var(--sgnl-alert-warning-text);
}

.banner-success {
	border-color: var(--sgnl-alert-success-border);
	background: var(--sgnl-alert-success-background-primary);
	color: var(--sgnl-alert-success-text);
}

.sticky-bar {
	width: 100%;
}
</style>
